import {
  isDocRef,
  isObject,
  ISODateType,
  TypeGuard,
} from '@principle-theorem/shared';
import { DocumentReference } from 'firebase-admin/firestore';
import { IPractice } from '../../practice/practice';
import { IAccClaimFundingRequest } from '../funding-request/acc/acc-claim';
import { IAccInvoiceFundingRequest } from '../funding-request/acc/acc-invoice';
import {
  AccConfirmedClaim,
  IAccClaim,
  IAccClaimStatus,
} from './claim/acc-claim';
import { AccClaimSubmissionResponseStatus } from './claim/acc-claim-enums';
import {
  AccDocumentStatus,
  IAccDocumentMetadata,
} from './document/acc-document';
import {
  IAccInvoice,
  IAccInvoiceStatus,
  IAccInvoiceStatusDetails,
} from './invoice/acc-invoice';
import {
  AccInvoiceProcessingStatus,
  AccInvoiceStatus,
} from './invoice/acc-invoice-enums';
import { IPatient } from '../../patient/patient';

export const ACC_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
export type AccDateTimeFormat = string;

export enum AccApiAction {
  SendDocument = 'sendDocument',
  SubmitClaim = 'submitClaim',
  SubmitInvoice = 'submitInvoice',
  SyncRequests = 'syncRequests',
}

/**
 * Submit Claim
 * POST
 * https://apiservicesnpe.acc.co.nz/api/v2/claims
 */

export interface IAccSubmitClaimData {
  claim: IAccClaim;
  testMode: boolean;
  claimRequestRef?: DocumentReference<IAccClaimFundingRequest>;
}

export interface IAccSubmitClaimRequest {
  action: AccApiAction.SubmitClaim;
  data: IAccSubmitClaimData;
}

export interface IAccSubmitClaimResponse {
  claim: AccConfirmedClaim;
  status: AccClaimSubmissionResponseStatus;
  createdAt: string; // check all unknown date formats
  lastUpdate: string; // check all unknown date formats
}

/**
 * Get (pre-allocate) Claim Number
 * GET
 * https://apiservicesnpe.acc.co.nz/api/v2/claims/claimNumber
 *
 * Pre-allocates a claim number that is valid for use for 60 days. Used to
 * guarantee we hold a claim number BEFORE submitting, so a claim can never be
 * left in a submitted-but-unidentifiable state when ACC omits the number from
 * the submit response.
 */

export interface IAccGetClaimNumberData {
  vendorId: string;
  providerId: string;
}

export interface IAccClaimNumberResponse {
  claimNumber: string;
  status?: string;
  formType?: string;
  allocatedVendorId?: string;
  allocatedProviderId?: string;
  usedVendorId?: string;
  usedProviderId?: string;
  formSubmissionId?: string;
}

/**
 * Get Claim Status
 * GET - Single Claims
 * https://apiservicesnpe.acc.co.nz/api/v2/claims/status
 */

export interface IAccGetSingleClaimStatusData {
  vendorId: string;
  providerId: string;
  claimNumber: string;
}

export interface IAccGetSingleClaimStatusResponse {
  claimStatusList: IAccClaimStatus[];
}

/**
 * Submit Invoice
 * POST
 * https://apiservicesnpe.acc.co.nz/api/v2/claims/vendors/invoice
 */

export interface IAccSubmitInvoiceData {
  invoice: IAccInvoice;
  invoiceRequestRef: DocumentReference<IAccInvoiceFundingRequest>;
  testMode: boolean;
}

export interface IAccSubmitInvoiceRequest {
  action: AccApiAction.SubmitInvoice;
  data: IAccSubmitInvoiceData;
}

export interface IAccSubmitInvoiceResponse {
  createdAt: ISODateType;
  invoice: IAccInvoice;
  invoiceId?: string;
  invoiceStatus: AccInvoiceStatus;
  lastUpdate: ISODateType;
}

/**
 * Invoice Status by Schedule ID
 * GET
 * https://apiservicesnpe.acc.co.nz/api/v2/claims/vendors/invoice/{scheduleId}
 */

export interface IAccGetInvoiceStatusData {
  vendorId: string;
  scheduleId: string;
}

export interface IAccGetInvoiceStatusResponse {
  invoiceStatusDetailsList: IAccInvoiceStatusDetails[];
}

/**
 * Query Invoices by vendor
 * GET
 * https://apiservicesnpe.acc.co.nz/api/v2/claims/vendors/invoices/batch
 */

export interface IAccQueryInvoiceStatusData {
  vendorId: string;
  providerId?: string;
  startDate?: ISODateType;
  endDate?: ISODateType;
  processingStatus?: AccInvoiceProcessingStatus[];
  invoiceNumber?: string;
  claimNumber?: string;
}

export interface IAccQueryInvoiceStatusResponse {
  invoiceStatusList: IAccInvoiceStatus[];
}

/**
 * Documents - PDF Only
 * POST - type:multipart/form-data
 * https://apiservicesnpe.acc.co.nz/api/v2/documents
 */

export interface IAccSendDocumentData {
  mediaPaths: string[];
  metadata: IAccDocumentMetadata;
  claimRequestRef: DocumentReference<IAccClaimFundingRequest>;
  testMode: boolean;
}

export interface IAccSendDocumentRequest {
  action: AccApiAction.SendDocument;
  data: IAccSendDocumentData;
}

export interface IAccSendDocumentResponse {
  documentMetaData: IAccDocumentMetadata;
  status: AccDocumentStatus;
  createdAt: ISODateType;
}

export interface IAccSyncRequestsData {
  patientRef?: DocumentReference<IPatient>;
  testMode: boolean;
}

export interface IAccSyncRequestsRequest {
  action: AccApiAction.SyncRequests;
  data: IAccSyncRequestsData;
}

export interface IAccApiRequest {
  practice: DocumentReference<IPractice>;
  request: AnyAccRequest;
}

export const isAccApiRequest = TypeGuard.interface<IAccApiRequest>({
  practice: isDocRef,
  request: TypeGuard.interface<AnyAccRequest>({
    action: TypeGuard.enumValue(AccApiAction),
    data: isObject,
  }),
});

export type AnyAccRequest =
  | IAccSubmitClaimRequest
  | IAccSubmitInvoiceRequest
  | IAccSendDocumentRequest
  | IAccSyncRequestsRequest;

export type AccApiResponse =
  | IAccGetSingleClaimStatusResponse
  | IAccQueryInvoiceStatusResponse
  | IAccSubmitClaimResponse
  | IAccSubmitInvoiceResponse
  | IAccSendDocumentResponse
  | IAccGetInvoiceStatusResponse;
